// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW: "Who" — the sex composition of the Order of Australia.
//
// The thesis of this view is the UNCERTAINTY. The register publishes no sex
// field; the only signal is the honorific a person was gazetted under, and that
// instrument fails hardest exactly where a claim would be most interesting
// (65.2% of Companions cannot be classified, against 16.3% of Medal
// recipients). So the "cannot be classified" group is never dropped, never
// faded and never relegated to a footnote: it is drawn as a full-weight band
// sitting BETWEEN the two arithmetic bounds on women's share, which is exactly
// what it is. A reader can then see for themselves that the ranges for the four
// levels overlap heavily, and therefore that the levels cannot be ranked from
// this data at all.

import type { Ctx, View } from '../types';
import { SEX_COLOUR } from '../palette';
import { num, pct, escapeHtml, longDate, tipAttr } from '../format';
import { gloss } from '../glossary';

interface Row {
  code: string;
  short: string;
  name: string;
  full: string;
  f: number;
  m: number;
  u: number;
  t: number;
}

const LEVEL_DEFS: { code: string; short: string; name: string; full: string; keys: string[] }[] = [
  { code: 'AC', short: 'AC', name: 'Companion', full: 'Companion (AC)', keys: ['AC'] },
  { code: 'AO', short: 'AO', name: 'Officer', full: 'Officer (AO)', keys: ['AO'] },
  { code: 'AM', short: 'AM', name: 'Member', full: 'Member (AM)', keys: ['AM'] },
  { code: 'OAM', short: 'OAM', name: 'Medal', full: 'Medal (OAM)', keys: ['OAM'] },
  {
    code: 'AKAD',
    short: 'AK/AD',
    name: 'Knight/Dame',
    full: 'Knight or Dame (AK/AD)',
    keys: ['AK', 'AD'],
  },
];

const INK = '#1b2330';

/** Safe division. Every ratio on this page goes through it. */
const frac = (a: number, b: number): number => (b > 0 && Number.isFinite(a) ? a / b : 0);

function niceMax(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  for (const s of [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]) {
    if (v <= s * mag) return s * mag;
  }
  return 10 * mag;
}

export const whoView: View = (root: HTMLElement, ctx: Ctx) => {
  const core = ctx.core;

  // ---------------------------------------------------------------- data ---
  const rows: Row[] = [];
  for (const def of LEVEL_DEFS) {
    let f = 0;
    let m = 0;
    let u = 0;
    let t = 0;
    for (const k of def.keys) {
      const s = core.sex[k];
      if (!s) continue;
      f += s.female;
      m += s.male;
      u += s.unknown;
      t += s.total;
    }
    if (t > 0) {
      rows.push({ code: def.code, short: def.short, name: def.name, full: def.full, f, m, u, t });
    }
  }

  const TOT = rows.reduce(
    (a, r) => ({ f: a.f + r.f, m: a.m + r.m, u: a.u + r.u, t: a.t + r.t }),
    { f: 0, m: 0, u: 0, t: 0 },
  );
  const totCls = TOT.f + TOT.m;
  const totClsShare = frac(totCls, TOT.t);
  const totFemOfCls = frac(TOT.f, totCls);
  const totLo = frac(TOT.f, TOT.t);
  const totHi = frac(TOT.f + TOT.u, TOT.t);

  const years = core.sexByYear.slice().sort((a, b) => a.year - b.year);
  const lastYear = years.length > 0 ? years[years.length - 1].year : 0;
  const asAt = ctx.meta.as_at_award_date ?? '';
  const asAtYear = Number(asAt.slice(0, 4));
  // Any year at or after the "as at" date is only partly gazetted.
  const partialFrom = Number.isFinite(asAtYear) && asAtYear > 0 ? asAtYear : lastYear;
  const hasPartial = years.some((y) => y.year >= partialFrom);

  const complete = years.filter((y) => y.year < partialFrom);
  const early = complete.slice(0, 5);
  const late = complete.slice(-5);

  interface Span {
    femShare: number;
    clsShare: number;
    from: number;
    to: number;
  }
  const span = (set: typeof complete): Span => {
    let f = 0;
    let m = 0;
    let u = 0;
    for (const y of set) {
      f += y.female;
      m += y.male;
      u += y.unknown;
    }
    return {
      femShare: frac(f, f + m),
      clsShare: frac(f + m, f + m + u),
      from: set.length > 0 ? set[0].year : 0,
      to: set.length > 0 ? set[set.length - 1].year : 0,
    };
  };
  const earlyS = span(early);
  const lateS = span(late);

  let bestYear = 0;
  let bestShare = 0;
  for (const y of complete) {
    const cl = y.female + y.male;
    if (cl < 50) continue;
    const sh = frac(y.female, cl);
    if (sh > bestShare) {
      bestShare = sh;
      bestYear = y.year;
    }
  }

  const acRow = rows.find((r) => r.code === 'AC');
  const oamRow = rows.find((r) => r.code === 'OAM');
  const acUnk = acRow ? frac(acRow.u, acRow.t) : 0;
  const oamUnk = oamRow ? frac(oamRow.u, oamRow.t) : 0;
  const acLo = acRow ? frac(acRow.f, acRow.t) : 0;
  const acHi = acRow ? frac(acRow.f + acRow.u, acRow.t) : 0;
  const oamCls = oamRow ? frac(oamRow.f + oamRow.m, oamRow.t) : 0;

  // --------------------------------------------------------------- state ---
  const params = new URLSearchParams(root.dataset.params ?? '');
  let selected = params.get('level') ?? '';
  if (!rows.some((r) => r.code === selected)) selected = '';
  let mode: 'share' | 'counts' = params.get('mode') === 'counts' ? 'counts' : 'share';

  // View state lives in the hash, but a redraw here is local — replaceState
  // keeps the URL shareable without bouncing the whole shell through a
  // hashchange (which would re-run the router and scroll the page to the top).
  const syncUrl = (): void => {
    const p = new URLSearchParams();
    if (selected !== '') p.set('level', selected);
    if (mode !== 'share') p.set('mode', mode);
    const q = p.toString();
    history.replaceState(null, '', `#/who${q === '' ? '' : `?${q}`}`);
  };

  // ------------------------------------------------- chart 1: the levels ---
  // The coordinate space is authored close to the width the chart actually
  // renders at on a desktop (~1200px inside the panel). An SVG with width="100%"
  // scales its whole coordinate system, TEXT INCLUDED: authored at 370 units
  // wide this chart upscaled 3.3x and rendered its 11px labels at 36px, big
  // enough to overlap the bars beneath them. Keeping the authored width near
  // the rendered width keeps type at its intended size.
  const A_W = 1120;
  const A_TOP = 34;
  const A_ROWH = 74;
  const A_BARH = 30;
  const A_X0 = 150;
  const A_BARW = A_W - A_X0 - 8;
  const A_H = A_TOP + rows.length * A_ROWH + 26;

  function levelChart(): string {
    if (rows.length === 0) return '<p class="empty">No level breakdown is available.</p>';
    const gx = (p: number): number => A_X0 + p * A_BARW;
    const plotBottom = A_TOP + rows.length * A_ROWH;

    let grid = '';
    for (const p of [0, 0.25, 0.5, 0.75, 1]) {
      const x = gx(p);
      const anchor = p === 0 ? 'start' : p === 1 ? 'end' : 'middle';
      grid +=
        `<line x1="${x.toFixed(1)}" y1="${A_TOP - 6}" x2="${x.toFixed(1)}" y2="${plotBottom}" stroke="#cfc9ba" stroke-width="1"/>` +
        `<text x="${x.toFixed(1)}" y="${A_TOP - 12}" font-size="11" text-anchor="${anchor}">${p * 100}%</text>`;
    }

    const body = rows
      .map((r, i) => {
        const y = A_TOP + i * A_ROWH;
        const barY = y + 2;
        const fFrac = frac(r.f, r.t);
        const uFrac = frac(r.u, r.t);
        const wF = fFrac * A_BARW;
        const wU = uFrac * A_BARW;
        const wM = Math.max(0, A_BARW - wF - wU);
        const cls = r.f + r.m;
        const clsShare = frac(cls, r.t);
        const hasEst = cls > 0;
        const est = frac(r.f, cls);
        const lo = fFrac;
        const hi = fFrac + uFrac;
        const isSel = selected === r.code;
        const dim = selected !== '' && !isSel ? 0.24 : 1;

        const tip = [
          `${r.full} — ${num(r.t)} awards`,
          `Women (Mrs, Ms, Miss): ${num(r.f)} (${pct(fFrac, 1)} of the level)`,
          `Title carries no sex: ${num(r.u)} (${pct(uFrac, 1)})`,
          `Men (Mr): ${num(r.m)} (${pct(frac(r.m, r.t), 1)})`,
          `Classifiable: ${num(cls)} of ${num(r.t)} (${pct(clsShare, 1)})`,
          hasEst ? `Of those classifiable, ${pct(est, 1)} are women` : 'None classifiable',
          `Women's true share of this level is between ${pct(lo, 1)} and ${pct(hi, 1)}`,
        ].join('\n');

        const aria = `${r.full}: ${num(r.t)} awards, ${pct(clsShare, 1)} classifiable from a title. Women are between ${pct(lo, 1)} and ${pct(hi, 1)} of the level.`;

        let diamond = '';
        if (hasEst) {
          const cx = A_X0 + est * A_BARW;
          const cy = barY + A_BARH / 2;
          const dTip = [
            `${r.full} — estimate: ${pct(est, 1)} women`,
            `This assumes the ${num(r.u)} recipients we cannot classify split by`,
            'sex the way the classified ones do. That is an assumption, not a',
            'measurement, and the higher the level the less it can be trusted.',
          ].join('\n');
          diamond =
            `<path d="M ${(cx - 5).toFixed(1)} ${cy} L ${cx.toFixed(1)} ${cy - 5} ` +
            `L ${(cx + 5).toFixed(1)} ${cy} L ${cx.toFixed(1)} ${cy + 5} Z" fill="${INK}" ` +
            `stroke="#ffffff" stroke-width="1.4" data-tip="${tipAttr(dTip)}" ` +
            `aria-label="${escapeHtml(`Estimate for ${r.full}: ${pct(est, 1)} women, assuming the unclassified split like the classified`)}"/>`;
        }

        return `<g class="who-row" data-level="${escapeHtml(r.code)}" tabindex="0" role="button"
            aria-pressed="${isSel ? 'true' : 'false'}" aria-label="${escapeHtml(aria)}"
            opacity="${dim}" data-tip="${tipAttr(tip)}">
          <rect class="who-focus" x="1" y="${y - 1}" width="${A_W - 2}" height="${A_ROWH - 4}" rx="4" fill="none" stroke="none"/>
          <text x="2" y="${y + 15}" font-size="12.5" font-weight="700" fill="${INK}">${escapeHtml(r.short)}</text>
          <text x="2" y="${y + 29}" font-size="11">${escapeHtml(r.name)}</text>
          <rect x="${A_X0}" y="${barY}" width="${wF.toFixed(2)}" height="${A_BARH}" fill="${SEX_COLOUR.female}"/>
          <rect x="${(A_X0 + wF).toFixed(2)}" y="${barY}" width="${wU.toFixed(2)}" height="${A_BARH}" fill="${SEX_COLOUR.unknown}"/>
          <rect x="${(A_X0 + wF + wU).toFixed(2)}" y="${barY}" width="${wM.toFixed(2)}" height="${A_BARH}" fill="${SEX_COLOUR.male}"/>
          ${diamond}
          <text x="${A_X0}" y="${barY + A_BARH + 12}" font-size="10.5">${escapeHtml(`${num(r.t)} awards · ${pct(clsShare, 1)} classifiable from a title`)}</text>
          <text x="${A_X0}" y="${barY + A_BARH + 24}" font-size="10.5">${escapeHtml(`women are ${pct(lo, 1)}–${pct(hi, 1)} of the level`)}</text>
        </g>`;
      })
      .join('');

    const half = gx(0.5);
    const ref =
      `<line x1="${half.toFixed(1)}" y1="${A_TOP - 6}" x2="${half.toFixed(1)}" y2="${plotBottom}" ` +
      `stroke="${INK}" stroke-width="1.2" stroke-dasharray="4 3" opacity="0.45" pointer-events="none"/>`;

    return `<svg class="chart" viewBox="0 0 ${A_W} ${A_H}" width="100%" role="group"
        aria-label="Composition of each level of the Order of Australia by inferred sex. The middle band is the recipients whose title carries no sex, drawn as the range women's true share could lie in.">
      ${grid}${body}${ref}
      <text x="${A_X0 + A_BARW / 2}" y="${A_H - 9}" font-size="11" text-anchor="middle">Share of that level’s awards</text>
      <text x="2" y="${A_H - 9}" font-size="10.5" fill="#7c8698">Order of Australia only</text>
    </svg>`;
  }

  // -------------------------------------------------- chart 2: the years ---
  // Authored near its rendered width for the same reason as chart 1.
  const C_W = 1120;
  const C_H = 380;
  const C_L = 56;
  const C_R = 16;
  const C_T = 22;
  const C_B = 52;
  const C_PW = C_W - C_L - C_R;
  const C_PH = C_H - C_T - C_B;

  function yearChart(): string {
    const n = years.length;
    if (n === 0) return '<p class="empty">No year-by-year figures are available.</p>';

    const colW = C_PW / n;
    const barW = Math.max(1, colW - 2.4);
    const baseY = C_T + C_PH;
    const yMaxCount = niceMax(Math.max(1, ...years.map((y) => y.female + y.male + y.unknown)));

    let grid = '';
    for (const p of [0, 0.25, 0.5, 0.75, 1]) {
      const gy = baseY - p * C_PH;
      const label = mode === 'share' ? `${p * 100}%` : num(Math.round(p * yMaxCount));
      grid +=
        `<line x1="${C_L}" y1="${gy.toFixed(1)}" x2="${C_L + C_PW}" y2="${gy.toFixed(1)}" stroke="#e6e2d8" stroke-width="1"/>` +
        `<text x="${C_L - 6}" y="${(gy + 3.5).toFixed(1)}" font-size="11" text-anchor="end">${label}</text>`;
    }

    let bars = '';
    let hatch = '';
    let hits = '';
    let xLabels = '';
    const pts: string[] = [];

    years.forEach((y, i) => {
      const t = y.female + y.male + y.unknown;
      const denom = mode === 'share' ? t : yMaxCount;
      const x = C_L + i * colW + 1.2;
      const hF = denom > 0 ? (y.female / denom) * C_PH : 0;
      const hU = denom > 0 ? (y.unknown / denom) * C_PH : 0;
      const hM = denom > 0 ? (y.male / denom) * C_PH : 0;
      const top = baseY - hF - hU - hM;
      const partial = y.year >= partialFrom;
      const cl = y.female + y.male;

      if (t > 0) {
        bars +=
          `<rect x="${x.toFixed(2)}" y="${(baseY - hF).toFixed(2)}" width="${barW.toFixed(2)}" height="${hF.toFixed(2)}" fill="${SEX_COLOUR.female}"/>` +
          `<rect x="${x.toFixed(2)}" y="${(baseY - hF - hU).toFixed(2)}" width="${barW.toFixed(2)}" height="${hU.toFixed(2)}" fill="${SEX_COLOUR.unknown}"/>` +
          `<rect x="${x.toFixed(2)}" y="${top.toFixed(2)}" width="${barW.toFixed(2)}" height="${hM.toFixed(2)}" fill="${SEX_COLOUR.male}"/>`;
      }
      if (partial && t > 0) {
        hatch +=
          `<rect x="${x.toFixed(2)}" y="${top.toFixed(2)}" width="${barW.toFixed(2)}" height="${(hF + hU + hM).toFixed(2)}" ` +
          `fill="url(#whoHatch)" stroke="${INK}" stroke-opacity="0.5" stroke-width="0.8" pointer-events="none"/>`;
      }
      if (mode === 'share' && cl > 0) {
        pts.push(`${(x + barW / 2).toFixed(2)},${(baseY - frac(y.female, cl) * C_PH).toFixed(2)}`);
      }

      const tip = [
        `${y.year}${partial ? ' — part-year, see note' : ''}: ${num(t)} Order of Australia awards`,
        `Women: ${num(y.female)} · Men: ${num(y.male)}`,
        `Title carries no sex: ${num(y.unknown)} (${pct(frac(y.unknown, t), 1)})`,
        `Classifiable: ${num(cl)} of ${num(t)} (${pct(frac(cl, t), 1)})`,
        cl > 0 ? `Of those classifiable, ${pct(frac(y.female, cl), 1)} are women` : 'None classifiable',
        `Women's true share of this year is between ${pct(frac(y.female, t), 1)} and ${pct(frac(y.female + y.unknown, t), 1)}`,
      ].join('\n');

      hits +=
        `<rect class="who-hit" x="${(C_L + i * colW).toFixed(2)}" y="${C_T}" width="${colW.toFixed(2)}" height="${C_PH}" ` +
        `fill="transparent" data-tip="${tipAttr(tip)}"/>`;

      if ((y.year - years[0].year) % 5 === 0) {
        xLabels += `<text x="${(C_L + i * colW + colW / 2).toFixed(1)}" y="${baseY + 16}" font-size="11" text-anchor="middle">${y.year}</text>`;
      }
    });

    const line =
      mode === 'share' && pts.length > 1
        ? `<polyline points="${pts.join(' ')}" fill="none" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round" pointer-events="none"/>`
        : '';

    const yTitle = mode === 'share' ? 'Share of that year’s awards' : 'Awards gazetted';
    const aria = `Order of Australia awards by year, ${years[0].year} to ${lastYear}, split into women, recipients whose title carries no sex, and men. Women's share of the classifiable group rose from ${pct(earlyS.femShare, 1)} across ${earlyS.from} to ${earlyS.to}, to ${pct(lateS.femShare, 1)} across ${lateS.from} to ${lateS.to}.`;

    return `<svg class="chart who-year" viewBox="0 0 ${C_W} ${C_H}" width="100%" role="img" aria-label="${escapeHtml(aria)}">
      <defs>
        <pattern id="whoHatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="${INK}" stroke-width="1.6" stroke-opacity="0.55"/>
        </pattern>
      </defs>
      ${grid}
      <line x1="${C_L}" y1="${baseY}" x2="${C_L + C_PW}" y2="${baseY}" stroke="#a49b86" stroke-width="1"/>
      ${bars}${line}${hatch}${hits}${xLabels}
      <text x="${C_L + C_PW / 2}" y="${C_H - 8}" font-size="11" text-anchor="middle">Year the award was gazetted</text>
      <text transform="translate(12 ${C_T + C_PH / 2}) rotate(-90)" font-size="11" text-anchor="middle">${escapeHtml(yTitle)}</text>
      <text x="${C_L + C_PW}" y="${C_H - 8}" font-size="10.5" text-anchor="end" fill="#7c8698">Order of Australia only</text>
    </svg>`;
  }

  // ----------------------------------------------------------- the table ---
  function table(): string {
    const body = rows
      .map((r) => {
        const cls = r.f + r.m;
        const isSel = selected === r.code;
        const dim = selected !== '' && !isSel ? '1' : '0';
        const tip = `${r.full}: ${num(r.f)} women and ${num(r.m)} men out of the ${num(cls)} recipients whose title carries sex. The other ${num(r.u)} are unknown and are not in this percentage.`;
        return `<tr data-dim="${dim}">
          <th scope="row" style="font-weight:400">
            <button type="button" class="who-pick" data-level="${escapeHtml(r.code)}" aria-pressed="${isSel ? 'true' : 'false'}"
              aria-label="${escapeHtml(`Highlight ${r.full} in the chart above`)}">${escapeHtml(r.full)}</button>
          </th>
          <td class="n">${num(r.t)}</td>
          <td class="n">${num(r.f)}</td>
          <td class="n">${num(r.m)}</td>
          <td class="n">${num(r.u)}</td>
          <td class="n">${pct(frac(cls, r.t), 1)}</td>
          <td class="n" data-tip="${tipAttr(tip)}">${cls > 0 ? pct(frac(r.f, cls), 1) : '—'}</td>
          <td class="n">${pct(frac(r.f, r.t), 1)}–${pct(frac(r.f + r.u, r.t), 1)}</td>
        </tr>`;
      })
      .join('');

    return `<div class="scroller"><table class="who-tbl">
      <caption class="panel-sub" style="text-align:left;padding-bottom:.5rem">
        Every Order of Australia award by level. The last two columns are the same quantity
        measured two ways: what we can see, and what is actually consistent with the data.
      </caption>
      <thead><tr>
        <th scope="col">Level</th>
        <th scope="col" class="n">Awards</th>
        <th scope="col" class="n">Women</th>
        <th scope="col" class="n">Men</th>
        <th scope="col" class="n">Title carries no sex</th>
        <th scope="col" class="n">Classifiable</th>
        <th scope="col" class="n">Women, of those classified</th>
        <th scope="col" class="n">Range women’s real share must lie in</th>
      </tr></thead>
      <tbody>${body}</tbody>
      <tfoot><tr>
        <th scope="row">All Order of Australia</th>
        <td class="n">${num(TOT.t)}</td>
        <td class="n">${num(TOT.f)}</td>
        <td class="n">${num(TOT.m)}</td>
        <td class="n">${num(TOT.u)}</td>
        <td class="n">${pct(totClsShare, 1)}</td>
        <td class="n">${pct(totFemOfCls, 1)}</td>
        <td class="n">${pct(totLo, 1)}–${pct(totHi, 1)}</td>
      </tr></tfoot>
    </table></div>`;
  }

  function selectionNote(): string {
    if (selected === '') {
      return `<p class="panel-sub" style="margin-top:.75rem">Nothing is selected. Choose a bar above or a
        level in the table to isolate it — the other levels dim so you can compare one range against the
        50% line.</p>`;
    }
    const r = rows.find((x) => x.code === selected);
    if (!r) return '';
    const cls = r.f + r.m;
    return `<div class="note" role="status">
      <strong>${escapeHtml(r.full)}.</strong> ${num(r.t)} awards, of which ${num(r.u)}
      (${pct(frac(r.u, r.t), 1)}) were gazetted under a title that carries no sex and cannot be classified
      at all. Of the ${num(cls)} that can be, ${cls > 0 ? pct(frac(r.f, cls), 1) : '—'} are women. Counting
      the unclassified group honestly, women are somewhere between
      <strong>${pct(frac(r.f, r.t), 1)}</strong> and <strong>${pct(frac(r.f + r.u, r.t), 1)}</strong> of
      this level, and nothing in this register narrows that any further.
      <button type="button" class="pill who-clear" style="min-height:44px;margin-left:.5rem">Clear selection</button>
    </div>`;
  }

  // ---------------------------------------------------------- the render ---
  // Selecting a level rebuilds this whole subtree, which would drop keyboard
  // focus on the floor. Whatever the reader activated is re-focused afterwards.
  let pendingFocus = '';

  function render(): void {
    root.innerHTML = `
      <style>
        .who-row { cursor: pointer; }
        .who-row:hover .who-focus { stroke: var(--border-strong); stroke-width: 1.2; }
        .who-row:focus-visible { outline: none; }
        .who-row:focus-visible .who-focus { stroke: var(--accent-secondary); stroke-width: 3; }
        .who-hit:hover { fill: rgba(27, 35, 48, 0.07); }
        /* 52 columns cannot shrink to 375px and keep legible axis text, so this
           one chart scrolls inside its .chart-wrap rather than scaling down. */
        .who-year { min-width: 640px; }
        .who-tbl tbody tr[data-dim='1'] { opacity: .45; }
        /* A selection control, deliberately NOT styled like a .glossary-link. */
        .who-pick {
          font: inherit; font-size: var(--font-size-sm); text-align: left; cursor: pointer;
          padding: .3rem .5rem; min-height: 40px; white-space: nowrap;
          background: var(--bg-surface); color: var(--text-primary);
          border: 1px solid var(--border-default); border-radius: var(--radius-sm);
        }
        .who-pick:hover { background: var(--bg-hover); }
        .who-pick[aria-pressed='true'] {
          background: var(--accent-secondary); border-color: var(--accent-secondary);
          color: #fff; font-weight: 600;
        }
        .who-tbl tbody tr:has(.who-pick[aria-pressed='true']) { background: var(--accent-wash); }
        .who-prose { max-width: 68ch; margin-bottom: .75rem; }
      </style>

      <div class="view-head">
        <h1>Who receives the Order of Australia?</h1>
        <p class="lede">The register publishes no sex or gender field. Everything below is inferred from the
          ${gloss('honorific')} a person was ${gloss('gazette', 'gazetted')} under — an instrument that is
          weak in a way that matters. This page shows what it can support, and is explicit about the bigger
          question it cannot answer.</p>
      </div>

      <div class="stats">
        <div class="stat">
          <div class="stat-value">${pct(totClsShare, 1)}</div>
          <div class="stat-label">of ${gloss('order', 'Order of Australia')} recipients have a title that
            carries sex — ${num(totCls)} of ${num(TOT.t)}. The rest are Dr, Professor, The Hon, a military
            rank, or no title at all.</div>
        </div>
        <div class="stat">
          <div class="stat-value">${pct(totFemOfCls, 1)}</div>
          <div class="stat-label">of those ${num(totCls)} classifiable recipients are women. That is a share
            of the ${pct(totClsShare, 1)} we can classify — not of everyone.</div>
        </div>
        <div class="stat">
          <div class="stat-value">${pct(bestShare, 1)}</div>
          <div class="stat-label">women’s highest share of the classifiable group in any complete year, in
            ${bestYear === 0 ? '—' : bestYear}. It was ${pct(earlyS.femShare, 1)} across
            ${earlyS.from}–${earlyS.to}.</div>
        </div>
        <div class="stat">
          <div class="stat-value">${pct(acUnk, 1)}</div>
          <div class="stat-label">of ${gloss('ac', 'Companions (AC)')} cannot be classified at all, against
            ${pct(oamUnk, 1)} of ${gloss('oam', 'Medal recipients (OAM)')}. The instrument fails hardest at
            the top.</div>
        </div>
      </div>

      <section class="panel">
        <div class="panel-head">
          <h2>What this page can tell you, and what it cannot</h2>
          <p class="panel-sub">Please read this before the charts. It is the most important thing here.</p>
        </div>
        <p class="who-prose"><strong>There is no sex field.</strong> The register records a name, an award, a
          date, a suburb and a ${gloss('citation')}. It does not record sex or gender, and this site does not
          ask anyone what theirs is. The only signal available is the title a person was published under.
          <em>Mr</em>, <em>Mrs</em>, <em>Ms</em> and <em>Miss</em> carry sex. <em>Dr</em>,
          <em>Professor</em>, <em>The Hon</em>, military ranks and entries with no title at all do not —
          and every one of those is counted here as <strong>not classifiable</strong>. Never guessed at,
          never quietly dropped from the denominator.</p>
        <p class="who-prose"><strong>The failures are not random, and that is the whole problem.</strong>
          ${pct(acUnk, 1)} of ${gloss('ac', 'Companions (AC)')} are unclassifiable, against ${pct(oamUnk, 1)}
          of ${gloss('oam', 'Medal recipients (OAM)')}, with ${gloss('ao', 'Officers (AO)')} and
          ${gloss('am', 'Members (AM)')} in between. Senior recipients are disproportionately professors,
          judges, doctors and officers, so they are gazetted under a professional title instead. That missing
          group is itself likely to be sex-skewed — those professions have their own histories — so we cannot
          assume it divides the way the classified group does.</p>
        <p class="who-prose"><strong>So we will not tell you that the gap widens as the honour gets
          higher.</strong> The all-time numbers make that claim tempting, and it does not survive testing.
          Restricted to recent years, women’s share of the recipients with a sex-carrying title is
          <em>not</em> monotonic across Companion → Officer → Member → Medal; and the unclassifiable residual
          is itself tangled up with level, so the four levels are not even being measured with the same
          instrument. The honest statement is that this data is too weak at the top of the Order to answer
          the question. If that is the answer you need, you need a source that <em>records</em> sex. This
          register is not one, and no amount of care with these numbers will turn it into one.</p>
        <p class="who-prose"><strong>What it does support.</strong> Of the ${num(totCls)} recipients whose
          title carries sex — ${pct(totClsShare, 1)} of the Order — ${pct(totFemOfCls, 1)} are women, so
          women are a clear minority of the group we can actually see. And inside that same classifiable
          group the direction of travel is not subtle: ${pct(earlyS.femShare, 1)} across
          ${earlyS.from}–${earlyS.to}, ${pct(lateS.femShare, 1)} across ${lateS.from}–${lateS.to}.</p>
        <div class="note">Every figure on this page is an <strong>Order of Australia</strong> measure:
          ${num(TOT.t)} awards, out of the ${num(core.totals.records)} records on the register. The other
          award systems are not included.</div>
      </section>

      <section class="panel">
        <div class="panel-head">
          <h2>Each level, with the not-knowing drawn in</h2>
          <p class="panel-sub">One bar per level, drawn as 100% of that level’s awards. Left to right: women,
            then the recipients whose title carries no sex, then men. The middle band sits there deliberately,
            because it is exactly the range women’s true share could fall in — its left edge is the answer if
            <em>every</em> unclassified recipient were a man, its right edge the answer if every one were a
            woman. The diamond marks what you get by assuming the unclassified group splits like the
            classified one. Hover, or tab to a bar, for exact counts.</p>
        </div>
        <div class="chart-wrap">${levelChart()}</div>
        <div class="legend">
          <span class="legend-item"><span class="swatch" style="background:${SEX_COLOUR.female}"></span>Women (Mrs, Ms, Miss)</span>
          <span class="legend-item"><span class="swatch" style="background:${SEX_COLOUR.unknown}"></span>Title carries no sex — the range the answer lies in</span>
          <span class="legend-item"><span class="swatch" style="background:${SEX_COLOUR.male}"></span>Men (Mr)</span>
          <span class="legend-item"><span class="swatch" style="background:${INK};transform:rotate(45deg)"></span>Estimate if the unclassified split like the classified</span>
          <span class="legend-item"><span class="swatch" style="background:transparent;border:0;border-left:2px dashed ${INK};border-radius:0"></span>50% reference line</span>
        </div>
        <p class="panel-sub" style="margin-top:.75rem">The bands overlap heavily, and the overlap <em>is</em>
          the finding. For Companions the arithmetic alone spans ${pct(acLo, 1)} to ${pct(acHi, 1)} — wide
          enough that it cannot even exclude a female majority. Nobody thinks that is the truth; the point is
          that titles cannot rule it out, so no ranking of the levels can be read off this chart. Only the
          Medal, where ${pct(oamCls, 1)} of recipients are classifiable, has a range narrow enough to carry
          much weight.</p>
        ${selectionNote()}
        ${table()}
      </section>

      <section class="panel">
        <div class="panel-head">
          <h2>The change over time</h2>
          <p class="panel-sub">The same three groups by the year each award was gazetted,
            ${years.length > 0 ? years[0].year : '—'}–${lastYear === 0 ? '—' : lastYear}. In <em>Share</em>
            the columns are scaled to 100%, so the bands mean what they mean above: the top of the plum band
            is the floor for women’s share that year, the top of the grey band is the ceiling, and the dark
            line is the estimate that assumes the unclassified split like the classified. In <em>Counts</em>
            the columns are raw award numbers, which shows how much the Order grew. Every column is hoverable
            for exact figures.</p>
        </div>
        <div class="controls">
          <div class="control-group" role="group" aria-label="How to scale the yearly columns">
            <button type="button" data-mode="share" aria-pressed="${mode === 'share'}">Share</button>
            <button type="button" data-mode="counts" aria-pressed="${mode === 'counts'}">Counts</button>
          </div>
        </div>
        <div class="chart-wrap">${yearChart()}</div>
        <div class="legend">
          <span class="legend-item"><span class="swatch" style="background:${SEX_COLOUR.female}"></span>Women</span>
          <span class="legend-item"><span class="swatch" style="background:${SEX_COLOUR.unknown}"></span>Title carries no sex</span>
          <span class="legend-item"><span class="swatch" style="background:${SEX_COLOUR.male}"></span>Men</span>
          ${
            mode === 'share'
              ? `<span class="legend-item"><span class="swatch" style="background:transparent;border:0;border-top:2px solid ${INK};border-radius:0"></span>Estimate if the unclassified split like the classified</span>`
              : ''
          }
          ${
            hasPartial
              ? `<span class="legend-item"><span class="swatch" style="background-image:repeating-linear-gradient(45deg, ${INK} 0 1.5px, transparent 1.5px 4px);background-color:var(--bg-elevated)"></span>${partialFrom}: part-year, awards to ${escapeHtml(longDate(asAt))}</span>`
              : ''
          }
        </div>
        <p class="panel-sub" style="margin-top:.75rem">Two things here are worth keeping apart. Women’s share
          of the classifiable group rose from ${pct(earlyS.femShare, 1)} in ${earlyS.from}–${earlyS.to} to
          ${pct(lateS.femShare, 1)} in ${lateS.from}–${lateS.to}, peaking at ${pct(bestShare, 1)} in
          ${bestYear === 0 ? '—' : bestYear}. Meanwhile the grey band has barely narrowed in fifty years: the
          classifiable share was ${pct(earlyS.clsShare, 1)} in ${earlyS.from}–${earlyS.to} and
          ${pct(lateS.clsShare, 1)} in ${lateS.from}–${lateS.to}. The measurement is not improving; only the
          thing being measured is changing.${
            hasPartial
              ? ` The hatched final column is a part-year and its shape should not be read as a turning point.`
              : ''
          }</p>
      </section>
    `;

    if (pendingFocus !== '') {
      root.querySelector<SVGGElement | HTMLElement>(pendingFocus)?.focus();
      pendingFocus = '';
    }

    // ------------------------------------------------------------ wiring ---
    const toggle = (code: string, focusSel: string): void => {
      if (code === '') return;
      selected = selected === code ? '' : code;
      pendingFocus = focusSel;
      syncUrl();
      render();
    };

    root.querySelectorAll<SVGGElement>('.who-row').forEach((g) => {
      const code = g.dataset.level ?? '';
      const sel = `.who-row[data-level="${code}"]`;
      g.addEventListener('click', () => toggle(code, sel));
      g.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle(code, sel);
        }
      });
    });

    root.querySelectorAll<HTMLButtonElement>('.who-pick').forEach((b) => {
      const code = b.dataset.level ?? '';
      b.addEventListener('click', () => toggle(code, `.who-pick[data-level="${code}"]`));
    });

    root.querySelector<HTMLButtonElement>('.who-clear')?.addEventListener('click', () => {
      const was = selected;
      selected = '';
      pendingFocus = was === '' ? '' : `.who-row[data-level="${was}"]`;
      syncUrl();
      render();
    });

    root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) => {
      b.addEventListener('click', () => {
        const next: 'share' | 'counts' = b.dataset.mode === 'counts' ? 'counts' : 'share';
        if (next === mode) return;
        mode = next;
        pendingFocus = `[data-mode="${next}"]`;
        syncUrl();
        render();
      });
    });
  }

  render();
  syncUrl();
};
